import React from 'react';
import styled, {css} from 'styled-components';

import {useMainContentHeight} from '../../page-structure/useMainContentHeight';
import {ScrollBar} from '../../shared-components/mixin/scroll/scrollBar';
import {WithSidebarContext} from '../../SidebarContext';
import {DrawerBtn} from '../custom-feature/drawerBtn/DrawerBtn';
import {Tab} from '../elements/tabs/Tab';
import {Aside} from './aside/Aside';

const MainTitle = styled.h1`
    font-size: var(--text-size-default);
    color: var(--default-text-color);
`;
const MainSubTitle = styled.h2`
    font-size: var(--text-size-small);
    color: var(--default-text-color);
`;

/**
 * @param {object} param0 - Props for the MainSection component.
 * @param {boolean} param0.sticky -
 * If true, makes the MainSection component sticky to the top of the screen.
 * Useful for keeping key elements like action buttons visible during scrolling, especially on pages without auto-save.
 */
const MainSection = styled.div`
    margin-bottom: var(--space-xl);
    ${({sticky}) => {
        if (sticky)
            return css`
                position: sticky;
                top: 0;
                width: calc(100% + (var(--grid-padding) * 2));
                transform: translate(calc(0px - var(--grid-padding)), calc(0px - var(--grid-padding)));
                margin: 0 0 calc(0px - var(--grid-padding));
                background: var(--app-background);
                z-index: var(--z-index-table-header-row);
                padding: var(--grid-padding);
            `;
    }}
`;

export const StickyHeaderSection = styled.div`
    --mask-height: 20px;
    --mask-width: 20px;

    &:not(${Aside.Wrapper} &) {
        width: calc(100% + var(--mask-width));
        margin-top: calc(0px - var(--grid-padding));
        margin-left: calc(0px - (var(--mask-width) / 2));
        padding: var(--grid-padding) calc(var(--mask-width) / 2) var(--space-md);

        position: sticky;
        top: calc(0px - var(--grid-padding));
        background: var(--app-background);
        z-index: var(--mask-z-index);

        &::after {
            content: '';
            height: var(--mask-height);
            width: 100%;
            position: absolute;
            bottom: calc(0px - var(--mask-height));
            background-image: linear-gradient(to top, #ffffff00, var(--app-background));
        }
    }
`;

const MainWrapper = styled(ScrollBar)`
    grid-area: main;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    padding: var(--grid-padding);

    @-moz-document url-prefix() {
        overflow-y: auto;
        scrollbar-color: var(--scroll-bar-background) var(--scroll-bar-thumb);
    }

    &:has(${MainTitle.toString()}:nth-child(1) + ${MainSubTitle.toString()}:nth-child(2)) {
        ${MainTitle} {
            margin-bottom: 0;
            padding-bottom: 0;
        }
    }
    &:has(${DrawerBtn.toString()}) {
        padding-right: calc(var(--space-xxl) + var(--space-md));
    }

    ${StickyHeaderSection} + ${MainSection} {
        margin-top: var(--space-md);
    }

    &:has(${StickyHeaderSection}) {
        ${Tab.Wrapper} {
            position: relative;
            z-index: calc(var(--mask-z-index) + 1);
        }
    }
`;

const Main = ({children}) => {
    const {targetRef} = useMainContentHeight();
    return (
        <WithSidebarContext>
            {({sidebarOpen, sidebarClosing}) => (
                <MainWrapper sidebarOpen={sidebarOpen} sidebarClosing={sidebarClosing} ref={targetRef}>
                    {children}
                </MainWrapper>
            )}
        </WithSidebarContext>
    );
};

Main.Wrapper = MainWrapper;
Main.Title = MainTitle;
Main.SubTitle = MainSubTitle;
Main.Section = MainSection;
Main.StickyHeader = StickyHeaderSection;

export {Main, MainSubTitle, MainTitle, MainWrapper};
